/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  GetKbDocumentStatsParams,
  ListKbDocumentsParams,
  ListKbTopLevelItemsParams,
  TLHTTPValidationError,
  TLKbDocumentStatsResponse,
  TLPaginatedResponseKbDocumentListItemResponse,
  TLPaginatedResponseKbTopLevelItemResponse,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary List Kb Documents
 */
export const listKbDocuments = (
  params?: ListKbDocumentsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedResponseKbDocumentListItemResponse>(
    { url: `/kb/documents`, method: 'GET', params, signal },
    options,
  );
};

export const getListKbDocumentsQueryKey = (params?: ListKbDocumentsParams) => {
  return ['kb', 'documents', ...(params ? [params] : [])] as const;
};

export const getListKbDocumentsQueryOptions = <
  TData = Awaited<ReturnType<typeof listKbDocuments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListKbDocumentsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listKbDocuments>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListKbDocumentsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listKbDocuments>>> = ({ signal }) =>
    listKbDocuments(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listKbDocuments>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListKbDocumentsQueryResult = NonNullable<Awaited<ReturnType<typeof listKbDocuments>>>;
export type ListKbDocumentsQueryError = ErrorType<TLHTTPValidationError>;

export function useListKbDocuments<
  TData = Awaited<ReturnType<typeof listKbDocuments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListKbDocumentsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listKbDocuments>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listKbDocuments>>,
          TError,
          Awaited<ReturnType<typeof listKbDocuments>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListKbDocuments<
  TData = Awaited<ReturnType<typeof listKbDocuments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListKbDocumentsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listKbDocuments>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listKbDocuments>>,
          TError,
          Awaited<ReturnType<typeof listKbDocuments>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListKbDocuments<
  TData = Awaited<ReturnType<typeof listKbDocuments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListKbDocumentsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listKbDocuments>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Kb Documents
 */

export function useListKbDocuments<
  TData = Awaited<ReturnType<typeof listKbDocuments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListKbDocumentsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listKbDocuments>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListKbDocumentsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Kb Documents
 */
export const prefetchListKbDocumentsQuery = async <
  TData = Awaited<ReturnType<typeof listKbDocuments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListKbDocumentsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listKbDocuments>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListKbDocumentsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Kb Documents
 */
export const invalidateListKbDocuments = async (
  queryClient: QueryClient,
  params?: ListKbDocumentsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListKbDocumentsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Get Kb Document Stats
 */
export const getKbDocumentStats = (
  params?: GetKbDocumentStatsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLKbDocumentStatsResponse>({ url: `/kb/documents/stats`, method: 'GET', params, signal }, options);
};

export const getGetKbDocumentStatsQueryKey = (params?: GetKbDocumentStatsParams) => {
  return ['kb', 'documents', 'stats', ...(params ? [params] : [])] as const;
};

export const getGetKbDocumentStatsQueryOptions = <
  TData = Awaited<ReturnType<typeof getKbDocumentStats>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetKbDocumentStatsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentStats>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetKbDocumentStatsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getKbDocumentStats>>> = ({ signal }) =>
    getKbDocumentStats(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getKbDocumentStats>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetKbDocumentStatsQueryResult = NonNullable<Awaited<ReturnType<typeof getKbDocumentStats>>>;
export type GetKbDocumentStatsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetKbDocumentStats<
  TData = Awaited<ReturnType<typeof getKbDocumentStats>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetKbDocumentStatsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentStats>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getKbDocumentStats>>,
          TError,
          Awaited<ReturnType<typeof getKbDocumentStats>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetKbDocumentStats<
  TData = Awaited<ReturnType<typeof getKbDocumentStats>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetKbDocumentStatsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentStats>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getKbDocumentStats>>,
          TError,
          Awaited<ReturnType<typeof getKbDocumentStats>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetKbDocumentStats<
  TData = Awaited<ReturnType<typeof getKbDocumentStats>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetKbDocumentStatsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentStats>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Kb Document Stats
 */

export function useGetKbDocumentStats<
  TData = Awaited<ReturnType<typeof getKbDocumentStats>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetKbDocumentStatsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentStats>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetKbDocumentStatsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Kb Document Stats
 */
export const prefetchGetKbDocumentStatsQuery = async <
  TData = Awaited<ReturnType<typeof getKbDocumentStats>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetKbDocumentStatsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentStats>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetKbDocumentStatsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Kb Document Stats
 */
export const invalidateGetKbDocumentStats = async (
  queryClient: QueryClient,
  params?: GetKbDocumentStatsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetKbDocumentStatsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary List Kb Top Level Items
 */
export const listKbTopLevelItems = (
  params?: ListKbTopLevelItemsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedResponseKbTopLevelItemResponse>(
    { url: `/kb/documents/top-level`, method: 'GET', params, signal },
    options,
  );
};

export const getListKbTopLevelItemsQueryKey = (params?: ListKbTopLevelItemsParams) => {
  return ['kb', 'documents', 'top-level', ...(params ? [params] : [])] as const;
};

export const getListKbTopLevelItemsQueryOptions = <
  TData = Awaited<ReturnType<typeof listKbTopLevelItems>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListKbTopLevelItemsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listKbTopLevelItems>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListKbTopLevelItemsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listKbTopLevelItems>>> = ({ signal }) =>
    listKbTopLevelItems(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listKbTopLevelItems>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListKbTopLevelItemsQueryResult = NonNullable<Awaited<ReturnType<typeof listKbTopLevelItems>>>;
export type ListKbTopLevelItemsQueryError = ErrorType<TLHTTPValidationError>;

export function useListKbTopLevelItems<
  TData = Awaited<ReturnType<typeof listKbTopLevelItems>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListKbTopLevelItemsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listKbTopLevelItems>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listKbTopLevelItems>>,
          TError,
          Awaited<ReturnType<typeof listKbTopLevelItems>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListKbTopLevelItems<
  TData = Awaited<ReturnType<typeof listKbTopLevelItems>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListKbTopLevelItemsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listKbTopLevelItems>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listKbTopLevelItems>>,
          TError,
          Awaited<ReturnType<typeof listKbTopLevelItems>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListKbTopLevelItems<
  TData = Awaited<ReturnType<typeof listKbTopLevelItems>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListKbTopLevelItemsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listKbTopLevelItems>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Kb Top Level Items
 */

export function useListKbTopLevelItems<
  TData = Awaited<ReturnType<typeof listKbTopLevelItems>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListKbTopLevelItemsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listKbTopLevelItems>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListKbTopLevelItemsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Kb Top Level Items
 */
export const prefetchListKbTopLevelItemsQuery = async <
  TData = Awaited<ReturnType<typeof listKbTopLevelItems>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListKbTopLevelItemsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listKbTopLevelItems>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListKbTopLevelItemsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Kb Top Level Items
 */
export const invalidateListKbTopLevelItems = async (
  queryClient: QueryClient,
  params?: ListKbTopLevelItemsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListKbTopLevelItemsQueryKey(params) }, options);

  return queryClient;
};
